<template>
  <div
    v-show="visible"
    ref="cursorEl"
    class="remote-cursor"
    :class="{ 'remote-cursor--clicking': clicking }"
    aria-hidden="true"
  ></div>
</template>

<script setup>
// RemoteCursor — TV-side floating pointer driven by the paired phone's
// trackpad. Stays hidden until the phone sends a `cursor` message; auto-
// hides after 2s of no movement so it doesn't sit on top of content.
//
// useRemoteControl forwards cursor messages as window CustomEvents so this
// component can stay decoupled from the WS layer:
//   - remote-cursor-move  { dx, dy }    — relative delta in phone-trackpad px
//   - remote-cursor-click {}            — synthesize a click at (x, y)
//
// Movement is scaled by CURSOR_SCALE so a comfortable phone-trackpad span
// covers the whole TV viewport; the cursor is clamped to the viewport.
//
// SMOOTHNESS / why this is a rAF render loop (not a per-message DOM write):
// the phone sends one cursor delta per animation frame, but on a low-power
// Android TV WebView those messages arrive jittery (bursts then gaps) over the
// WS. Painting the cursor only when a message lands therefore stutters, and a
// fixed-duration CSS `transform` transition fights the irregular cadence and
// adds lag. Instead we keep a TARGET position (updated per message) and a
// separate RENDER position eased toward it on a steady requestAnimationFrame
// clock, writing `transform` straight to the DOM. The cursor glides smoothly
// across message gaps, and neither the target updates nor the per-frame paints
// pay Vue's reactive re-render cost.

import { onMounted, onUnmounted, ref } from 'vue'

const CURSOR_SCALE = 2.5
const IDLE_HIDE_MS = 2000
const CLICK_FLASH_MS = 220
// Fraction of the remaining gap the render position closes each frame. ~0.5
// reaches the target in a few frames (~50 ms at 60 fps) — smooth over jittery
// message arrival without feeling laggy. Higher = snappier, lower = smoother.
const SMOOTHING = 0.5
// Snap when within this many px of the target so the eased motion doesn't
// creep with an endless sub-pixel tail.
const SNAP_EPS = 0.4

const visible = ref(false)
const clicking = ref(false)
const cursorEl = ref(null)

// Position state lives OUTSIDE Vue reactivity — see the header comment. The
// rAF loop writes the transform directly, so these are plain numbers.
let targetX = 0
let targetY = 0
let renderX = 0
let renderY = 0
let rafId = 0

let idleTimer = null
let clickFlashTimer = null

function viewport() {
  return {
    w: window.innerWidth || document.documentElement.clientWidth || 0,
    h: window.innerHeight || document.documentElement.clientHeight || 0,
  }
}

function clampTarget() {
  const { w, h } = viewport()
  if (targetX < 0) targetX = 0
  if (targetY < 0) targetY = 0
  if (targetX > w - 1) targetX = w - 1
  if (targetY > h - 1) targetY = h - 1
}

// Write the current render position to the DOM. Direct style write (no Vue
// reactive binding) keeps per-frame cost to a single compositor-friendly
// transform update.
function paint() {
  const el = cursorEl.value
  if (el) el.style.transform = `translate3d(${renderX}px, ${renderY}px, 0)`
}

// Per-frame easing toward the target. Runs while the cursor is visible; stops
// itself once hidden so an idle cursor doesn't keep the main thread busy.
function tick() {
  const dx = targetX - renderX
  const dy = targetY - renderY
  if (Math.abs(dx) < SNAP_EPS && Math.abs(dy) < SNAP_EPS) {
    renderX = targetX
    renderY = targetY
  } else {
    renderX += dx * SMOOTHING
    renderY += dy * SMOOTHING
  }
  paint()
  rafId = visible.value ? requestAnimationFrame(tick) : 0
}

function startLoop() {
  if (!rafId) rafId = requestAnimationFrame(tick)
}

function scheduleHide() {
  if (idleTimer) clearTimeout(idleTimer)
  idleTimer = setTimeout(() => {
    visible.value = false
  }, IDLE_HIDE_MS)
}

function showAt(centerOnFirstUse = false) {
  if (centerOnFirstUse && !visible.value) {
    const { w, h } = viewport()
    targetX = renderX = w / 2
    targetY = renderY = h / 2
    paint()
  }
  visible.value = true
  scheduleHide()
  startLoop()
}

function onMove(event) {
  const detail = event?.detail || {}
  const dx = Number(detail.dx) || 0
  const dy = Number(detail.dy) || 0
  if (dx === 0 && dy === 0) return
  showAt(true)
  targetX += dx * CURSOR_SCALE
  targetY += dy * CURSOR_SCALE
  clampTarget()
}

function flashClick() {
  clicking.value = true
  if (clickFlashTimer) clearTimeout(clickFlashTimer)
  clickFlashTimer = setTimeout(() => {
    clicking.value = false
  }, CLICK_FLASH_MS)
}

// Synthesize a real DOM click at the cursor's position. Use both pointer
// + mouse events so handlers bound to either family fire. We deliberately
// dispatch on the deepest element under the cursor so component-level
// handlers (links, buttons, custom widgets) receive the event normally.
function synthesizeClick() {
  const { w, h } = viewport()
  if (renderX < 0 || renderY < 0 || renderX > w || renderY > h) return
  const target = document.elementFromPoint(renderX, renderY)
  if (!target) return
  const eventInit = {
    bubbles: true,
    cancelable: true,
    composed: true,
    view: window,
    clientX: renderX,
    clientY: renderY,
    button: 0,
    buttons: 1,
  }
  try {
    target.dispatchEvent(new MouseEvent('mousedown', eventInit))
    target.dispatchEvent(new MouseEvent('mouseup', { ...eventInit, buttons: 0 }))
    target.dispatchEvent(new MouseEvent('click', { ...eventInit, buttons: 0 }))
  } catch { /* ignore — element rejected the synthesized event */ }
}

function onClick() {
  showAt(true)
  // Land the click at the latest intended position even if the eased render
  // is a frame or two behind, and snap the visible cursor there to match.
  renderX = targetX
  renderY = targetY
  paint()
  flashClick()
  synthesizeClick()
}

function onResize() {
  clampTarget()
  const { w, h } = viewport()
  if (renderX > w - 1) renderX = w - 1
  if (renderY > h - 1) renderY = h - 1
  paint()
}

onMounted(() => {
  const { w, h } = viewport()
  targetX = renderX = w / 2
  targetY = renderY = h / 2
  paint()
  window.addEventListener('remote-cursor-move', onMove)
  window.addEventListener('remote-cursor-click', onClick)
  window.addEventListener('resize', onResize)
})

onUnmounted(() => {
  if (rafId) cancelAnimationFrame(rafId)
  if (idleTimer) clearTimeout(idleTimer)
  if (clickFlashTimer) clearTimeout(clickFlashTimer)
  window.removeEventListener('remote-cursor-move', onMove)
  window.removeEventListener('remote-cursor-click', onClick)
  window.removeEventListener('resize', onResize)
})
</script>

<style scoped>
.remote-cursor {
  position: fixed;
  top: 0;
  left: 0;
  width: 36px;
  height: 36px;
  margin: -18px 0 0 -18px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow:
    0 0 0 2px rgba(0, 0, 0, 0.35),
    0 6px 18px rgba(0, 0, 0, 0.5);
  pointer-events: none;
  z-index: 2147483647;
  /* No `transform` transition: the rAF loop already eases position every
     frame, and a fixed-duration transition on a per-frame-updated transform
     fights that cadence and stutters on low-power TV WebViews. Keep the
     opacity + size transitions for show/hide and the click flash. */
  transition: opacity 220ms ease, width 160ms ease, height 160ms ease;
  will-change: transform;
}
.remote-cursor--clicking {
  width: 28px;
  height: 28px;
  margin: -14px 0 0 -14px;
  background: rgba(96, 165, 250, 1);
  box-shadow:
    0 0 0 3px rgba(96, 165, 250, 0.35),
    0 0 24px rgba(96, 165, 250, 0.6);
}
</style>
